import { buildUrl } from '../lib/helpers/miscHelpers'
import { StringMap } from '../types/misc'
import { PARAMS } from './params'
import { SEARCH_PARAMS } from './searchParams'

export const ROUTES = {
  index: '/',

  /**
   * ---------------------------------------------------------------------------
   * Account
   * ---------------------------------------------------------------------------
   */
  _account: '/account',
  account: () => '/account',
  _forgotPassword: '/account/forgot-password',
  forgotPassword: () => ROUTES._forgotPassword,
  _register: '/account/register',
  register: () => ROUTES._register,
  _resetPassword: '/account/reset-password/:token',
  _emailVerificationRequired: '/account/email-verification-required',
  emailVerificationRequired: () => ROUTES._emailVerificationRequired,
  _verifyEmail: '/account/verify-email/:token',
  verifyEmail: (context: RouteContext<typeof PARAMS.verifyEmail>) =>
    buildUrl(ROUTES._verifyEmail, context),

  /**
   * ---------------------------------------------------------------------------
   * Auth
   * ---------------------------------------------------------------------------
   */
  _login: '/auth/login',
  login: () => ROUTES._login,
  logout: '/auth/logout',

  /**
   * ---------------------------------------------------------------------------
   * Contacts
   * ---------------------------------------------------------------------------
   */
  _addContact: `/contacts/add`,
  addContact: (
    context: RouteContext<undefined, typeof SEARCH_PARAMS.addContact>,
  ) => buildUrl(ROUTES._addContact, context),

  _editContact: `/contacts/:${PARAMS.editContact.contactId}/edit`,
  editContact: (context: RouteContext<typeof PARAMS.editContact>) =>
    buildUrl(ROUTES._editContact, context),

  _contactList: '/contacts/list',
  contactList: (
    context?: RouteContext<undefined, typeof SEARCH_PARAMS.contactList>,
  ) => buildUrl(ROUTES._contactList, context),

  _contactDetails: `/contacts/:${PARAMS.contactDetails.contactId}/details`,
  contactDetails: (context: RouteContext<typeof PARAMS.contactDetails>) =>
    buildUrl(ROUTES._contactDetails, context),

  contacts: {
    add: '/contacts/add',
    edit: '/contacts/:contactId/edit',
  },

  /**
   * ---------------------------------------------------------------------------
   * Debug
   * ---------------------------------------------------------------------------
   */
  _debug: '/debug',
  debug: () => ROUTES._debug,
  _debugSentry: '/debug/sentry-test',
  debugSentry: () => ROUTES._debugSentry,
  _debugRelease: '/debug/release',
  debugRelease: () => ROUTES._debugRelease,

  /**
   * ---------------------------------------------------------------------------
   * Settings
   * ---------------------------------------------------------------------------
   */
  _updatePassword: '/settings/update-password',
  updatePassword: '/settings/update-password',

  /**
   * ---------------------------------------------------------------------------
   * Tags
   * ---------------------------------------------------------------------------
   */
  _addTag: `/tags/add`,
  addTag: () => buildUrl(ROUTES._addTag),

  _tagDetails: `/tags/:${PARAMS.tagDetails.tagId}/details`,
  tagDetails: (context: RouteContext<typeof PARAMS.tagDetails>) =>
    buildUrl(ROUTES._tagDetails, context),

  _editTag: '/tags/:tagId/edit',
  editTag: (context: RouteContext<typeof PARAMS.editTag>) =>
    buildUrl(ROUTES._editTag, context),

  _tagList: '/tags/list',
  tagList: () => buildUrl(ROUTES._tagList),
}

export type RouteKey = keyof typeof ROUTES
export type RoutePath = (typeof ROUTES)[RouteKey]

type RouteContext<
  TParams extends StringMap | undefined = undefined,
  TSearchParams extends StringMap | undefined = undefined,
> = {
  params?: TParams extends StringMap ? TParams : undefined
  searchParams?: TSearchParams extends StringMap
    ? Partial<TSearchParams>
    : undefined
}
